// Findings #182: "Pesada del día". The crew weighs a unit's ration in the
// morning and the leftover at the end of the day; what was fed is the
// difference. The morning entry is a normal FeedLog whose feedKg is the
// weighed amount; the leftover updates the same FeedLog (feedKg = pesado −
// sobrante). Both weights live in `FeedLog.metadata.dayWeighing` (no
// migration). Without a leftover the weighed amount counts as fed and the
// office sees "sin sobrante".

export type DayWeighing = { weighedKg: number; leftoverKg: number | null }

type WithMetadata = { metadata?: Record<string, unknown> | null }

const round3 = (value: number): number => Math.round(value * 1000) / 1000

const nonNegative = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

export const readDayWeighing = (log: WithMetadata): DayWeighing | null => {
  const raw = log.metadata && typeof log.metadata === 'object' ? log.metadata.dayWeighing : undefined
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const record = raw as Record<string, unknown>
  const weighedKg = nonNegative(record.weighedKg)
  if (weighedKg === null || weighedKg <= 0) return null
  return { weighedKg, leftoverKg: nonNegative(record.leftoverKg) }
}

export const withDayWeighing = (
  metadata: Record<string, unknown> | null | undefined,
  weighing: DayWeighing
): Record<string, unknown> => ({ ...(metadata ?? {}), dayWeighing: { ...weighing } })

/** What was fed: the weighed amount less the leftover (the whole amount until the leftover is recorded). */
export const fedFromWeighing = (weighing: DayWeighing): number => round3(weighing.weighedKg - (weighing.leftoverKg ?? 0))

/**
 * A leftover typed by the crew: empty means nothing was left (0); otherwise a
 * number from 0 up to the weighed amount.
 */
export const parseLeftover = (draft: string, weighedKg: number): { ok: true; kg: number } | { ok: false } => {
  const trimmed = draft.trim().replace(',', '.')
  if (!trimmed) return { ok: true, kg: 0 }
  const kg = Number(trimmed)
  return Number.isFinite(kg) && kg >= 0 && kg <= weighedKg ? { ok: true, kg: round3(kg) } : { ok: false }
}
